import React, { useState } from 'react';
import { X, Clock, Bell, Check } from 'lucide-react';
import { API_URL } from '../services/apiConfig';

interface SnoozeModalProps {
  isOpen: boolean;
  onClose: () => void;
  notificationTitle?: string;
  notificationBody?: string;
}

const SnoozeModal: React.FC<SnoozeModalProps> = ({
  isOpen,
  onClose,
  notificationTitle = "🔔 साधना का समय",
  notificationBody = "राम नाम जपने का समय हो गया है 🙏"
}) => {
  const [customMinutes, setCustomMinutes] = useState<number>(30);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [triggerTime, setTriggerTime] = useState<string>('');


  const handleSnooze = async (minutes: number) => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/reminders/snooze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          snooze_minutes: minutes,
          original_title: notificationTitle,
          original_body: notificationBody
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(true);
        setTriggerTime(data.trigger_time);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        alert(data.detail || 'स्नूज़ करने में error हुआ');
      }
    } catch (error) {
      console.error('Snooze error:', error);
      alert('स्नूज़ करने में error हुआ');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={24} />
              <h2 className="text-lg font-bold">स्नूज़ करें</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              data-testid="snooze-close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-orange-100 mt-1">कितने समय बाद याद दिलाएं?</p>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <Check size={32} className="text-green-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">स्नूज़ सेट हो गया!</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {triggerTime} बजे याद दिलाएंगे 🙏
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {/* Quick Options */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleSnooze(5)}
                disabled={loading}
                className="p-3 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 rounded-xl text-center transition-colors disabled:opacity-50"
                data-testid="snooze-5-btn"
              >
                <Clock size={20} className="mx-auto text-orange-500 mb-1" />
                <span className="text-sm font-medium text-gray-800 dark:text-white">5 मिनट</span>
              </button>
              
              <button
                onClick={() => handleSnooze(10)}
                disabled={loading}
                className="p-3 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 rounded-xl text-center transition-colors disabled:opacity-50"
                data-testid="snooze-10-btn"
              >
                <Clock size={20} className="mx-auto text-orange-500 mb-1" />
                <span className="text-sm font-medium text-gray-800 dark:text-white">10 मिनट</span>
              </button>
              
              <button
                onClick={() => handleSnooze(15)}
                disabled={loading}
                className="p-3 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 rounded-xl text-center transition-colors disabled:opacity-50"
                data-testid="snooze-15-btn"
              >
                <Clock size={20} className="mx-auto text-orange-500 mb-1" />
                <span className="text-sm font-medium text-gray-800 dark:text-white">15 मिनट</span>
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-gray-200 dark:bg-stone-700"></div>
              <span className="text-xs text-gray-400">या</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-stone-700"></div>
            </div>

            {/* Custom Time */}
            <div className="bg-gray-50 dark:bg-stone-800 rounded-xl p-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                कस्टम समय (मिनट)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(Math.max(1, Math.min(120, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-3 py-2 bg-white dark:bg-stone-700 rounded-lg border border-gray-200 dark:border-stone-600 text-gray-800 dark:text-white text-center text-lg font-bold"
                  data-testid="snooze-custom-input"
                />
                <button
                  onClick={() => handleSnooze(customMinutes)}
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-lg disabled:opacity-50 transition-all hover:shadow-lg"
                  data-testid="snooze-custom-btn"
                >
                  {loading ? '...' : 'सेट करें'}
                </button>
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={onClose}
              className="w-full py-3 text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              data-testid="snooze-cancel-btn"
            >
              रद्द करें
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SnoozeModal;
